import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios'
import { useAuthStore } from '@/store/authStore'

// Create axios instance
export const api: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api/v1',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const token = useAuthStore.getState().token
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as any
    
    // Handle 401 errors (unauthorized) - token expired or invalid
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true
      
      // Skip refresh for auth endpoints to avoid infinite loops
      const isAuthEndpoint = originalRequest.url?.includes('/auth/')
      
      if (!isAuthEndpoint) {
        try {
          // Try to refresh token
          await useAuthStore.getState().refreshAuth()
          
          // Retry original request with new token
          const token = useAuthStore.getState().token
          if (token) {
            originalRequest.headers.Authorization = `Bearer ${token}`
            return api(originalRequest)
          }
        } catch (refreshError) {
          // If refresh fails, logout user and redirect to appropriate login page
          console.error('Token refresh failed:', refreshError)
          const user = useAuthStore.getState().user
          useAuthStore.getState().logout()
          
          // Only redirect if not already on a login page
          if (!window.location.pathname.includes('/login') && 
              !window.location.pathname.includes('/nfcrwacontrol') &&
              !window.location.pathname.includes('/user-login')) {
            // Determine correct login page based on user role (priority) or current route
            let redirectPath = '/user-login' // Default for staff, department_head, front_worker, etc.
            
            if (user?.role === 'universal_admin' || user?.role === 'office_admin') {
              redirectPath = '/nfcrwacontrol'
            } else if (user?.role === 'member') {
              redirectPath = '/login'
            } else if (window.location.pathname.includes('/admin') || 
                       window.location.pathname.includes('/nfcrwacontrol') || 
                       window.location.pathname.includes('/reports') ||
                       window.location.pathname.includes('/analytics')) {
              redirectPath = '/nfcrwacontrol'
            } else if (window.location.pathname.includes('/staff') || 
                       window.location.pathname.includes('/department')) {
              redirectPath = '/user-login'
            }
            
            window.location.href = redirectPath
          }
          return Promise.reject(refreshError)
        }
      } else {
        // For auth endpoints, just reject the error
        // If it's a token expired error, logout and redirect
        const errorData = error.response?.data as any
        if (errorData?.error === 'TOKEN_EXPIRED' || errorData?.error === 'INVALID_TOKEN') {
          const user = useAuthStore.getState().user
          useAuthStore.getState().logout()
          if (!window.location.pathname.includes('/login') && 
              !window.location.pathname.includes('/nfcrwacontrol') &&
              !window.location.pathname.includes('/user-login')) {
            // Determine correct login page based on user role (priority) or current route
            let redirectPath = '/user-login' // Default for staff, department_head, front_worker, etc.
            
            if (user?.role === 'universal_admin' || user?.role === 'office_admin') {
              redirectPath = '/nfcrwacontrol'
            } else if (user?.role === 'member') {
              redirectPath = '/login'
            } else if (window.location.pathname.includes('/admin') || 
                       window.location.pathname.includes('/nfcrwacontrol') || 
                       window.location.pathname.includes('/reports') ||
                       window.location.pathname.includes('/analytics')) {
              redirectPath = '/nfcrwacontrol'
            } else if (window.location.pathname.includes('/staff') || 
                       window.location.pathname.includes('/department')) {
              redirectPath = '/user-login'
            }
            
            window.location.href = redirectPath
          }
        }
      }
    }
    
    return Promise.reject(error)
  }
)

// Error handler
export const handleApiError = (error: any): string => {
  if (error.response?.data?.message) {
    return error.response.data.message
  }
  
  if (error.message) {
    return error.message
  }
  
  if (error.response?.status === 404) {
    return 'Resource not found'
  }
  
  if (error.response?.status === 500) {
    return 'Internal server error'
  }
  
  if (error.code === 'NETWORK_ERROR') {
    return 'Network error. Please check your connection.'
  }
  
  if (error.code === 'TIMEOUT_ERROR') {
    return 'Request timeout. Please try again.'
  }
  
  return 'An unexpected error occurred'
}

// API response wrapper
export const apiResponse = <T>(response: AxiosResponse<T>): T => {
  return response.data
}

// API error wrapper
export const apiError = (error: any): never => {
  throw new Error(handleApiError(error))
}

// Request helpers
export const apiGet = async <T>(url: string, config?: any): Promise<T> => {
  try {
    const response = await api.get<T>(url, config)
    return apiResponse(response)
  } catch (error) {
    apiError(error)
  }
}

export const apiPost = async <T>(url: string, data?: any, config?: any): Promise<T> => {
  try {
    const response = await api.post<T>(url, data, config)
    return apiResponse(response)
  } catch (error) {
    apiError(error)
  }
}

export const apiPut = async <T>(url: string, data?: any, config?: any): Promise<T> => {
  try {
    const response = await api.put<T>(url, data, config)
    return apiResponse(response)
  } catch (error) {
    apiError(error)
  }
}

export const apiPatch = async <T>(url: string, data?: any, config?: any): Promise<T> => {
  try {
    const response = await api.patch<T>(url, data, config)
    return apiResponse(response)
  } catch (error) {
    apiError(error)
  }
}

export const apiDelete = async <T>(url: string, config?: any): Promise<T> => {
  try {
    const response = await api.delete<T>(url, config)
    return apiResponse(response)
  } catch (error) {
    apiError(error)
  }
}

// File upload helper
export const apiUpload = async <T>(url: string, formData: FormData, config?: any): Promise<T> => {
  try {
    const response = await api.post<T>(url, formData, {
      ...config,
      headers: {
        'Content-Type': 'multipart/form-data',
        ...config?.headers,
      },
    })
    return apiResponse(response)
  } catch (error) {
    apiError(error)
  }
}

// Download helper
export const apiDownload = async (url: string, filename?: string, config?: any): Promise<void> => {
  try {
    const response = await api.get(url, {
      ...config,
      responseType: 'blob',
    })
    
    const blob = new Blob([response.data])
    const downloadUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = downloadUrl
    link.download = filename || 'download'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(downloadUrl)
  } catch (error) {
    apiError(error)
  }
}

// Query parameter builder
export const buildQueryParams = (params: Record<string, any>): string => {
  const searchParams = new URLSearchParams()
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      if (Array.isArray(value)) {
        value.forEach(v => searchParams.append(key, v))
      } else {
        searchParams.append(key, String(value))
      }
    }
  })
  
  return searchParams.toString()
}

// URL builder with query parameters
export const buildUrl = (baseUrl: string, params?: Record<string, any>): string => {
  if (!params) return baseUrl
  
  const queryString = buildQueryParams(params)
  return queryString ? `${baseUrl}?${queryString}` : baseUrl
}
